import React, { useEffect, useState } from 'react';
import { ClipboardList, Search, Eye, X, Loader2, Calendar, Activity, User, RefreshCw, Plus, Trash2, ArrowLeftRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const cap = (s) => (s || '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const AdminUserRoutines = () => {
  const token = localStorage.getItem('session_token') || localStorage.getItem('token');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [regeneratingDay, setRegeneratingDay] = useState(null); // day_number actualmente regenerándose
  // Estados de edición manual
  const [libraryExercises, setLibraryExercises] = useState([]);
  const [addingToDayModal, setAddingToDayModal] = useState(null); // { day_number } — abre modal de selección
  const [addSection, setAddSection] = useState('principal');
  const [addSearch, setAddSearch] = useState('');
  const [addingExercise, setAddingExercise] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null); // `${day}-${exercise_id}` actualmente eliminando

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/user-routines`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error('Error al cargar rutinas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openDetail = async (routineId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/user-routines/${routineId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDetail(res.data);
    } catch (err) {
      toast.error('Error al cargar el detalle');
    } finally {
      setLoadingDetail(false);
    }
  };

  const regenerateDay = async (dayNumber) => {
    if (!detail?.user?.user_id && !detail?.routine?.user_id) {
      toast.error('No se pudo identificar al usuario');
      return;
    }
    const userId = detail.user?.user_id || detail.routine?.user_id;
    if (!window.confirm(`¿Regenerar el día ${dayNumber}? Los demás días se mantienen igual.`)) return;
    setRegeneratingDay(dayNumber);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/regenerate-day/${dayNumber}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `Día ${dayNumber} regenerado`);
      // Refrescar el detalle con la rutina actualizada
      if (res.data?.routine) {
        setDetail({ ...detail, routine: res.data.routine });
      }
      // Refrescar la lista
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al regenerar el día');
    } finally {
      setRegeneratingDay(null);
    }
  };

  const loadLibrary = async () => {
    if (libraryExercises.length > 0) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/library-exercises`, { headers: { Authorization: `Bearer ${token}` } });
      setLibraryExercises(res.data.items || []);
    } catch (err) {
      toast.error('Error al cargar la biblioteca de ejercicios');
    }
  };

  const openAddModal = async (dayNumber) => {
    setAddingToDayModal({ day_number: dayNumber });
    setAddSection('principal');
    setAddSearch('');
    await loadLibrary();
  };

  const getUserId = () => detail?.user?.user_id || detail?.routine?.user_id;

  const openReplaceModal = async (dayNumber, ex) => {
    setAddingToDayModal({ day_number: dayNumber, replace: ex });
    setAddSection(ex.section || 'principal');
    setAddSearch('');
    await loadLibrary();
  };

  // exercise_id -> días donde ya está (solo se bloquea repetir dentro del MISMO día)
  const usedInRoutine = {};
  (detail?.routine?.days || []).forEach(d => (d.exercises || []).forEach(e => {
    if (!e.exercise_id) return;
    usedInRoutine[e.exercise_id] = usedInRoutine[e.exercise_id] || [];
    if (!usedInRoutine[e.exercise_id].includes(d.day_number)) usedInRoutine[e.exercise_id].push(d.day_number);
  }));

  const replaceExercise = async (new_exercise_id) => {
    const userId = getUserId();
    const modal = addingToDayModal;
    if (!userId || !modal?.replace) return;
    setAddingExercise(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/routine/day/${modal.day_number}/exercise/${modal.replace.exercise_id}/replace`,
        { new_exercise_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ejercicio cambiado');
      if (res.data?.routine) setDetail({ ...detail, routine: res.data.routine });
      setAddingToDayModal(null);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar el ejercicio');
    } finally {
      setAddingExercise(false);
    }
  };

  const addExercise = async (exercise_id) => {
    const userId = getUserId();
    if (!userId || !addingToDayModal) return;
    setAddingExercise(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/routine/day/${addingToDayModal.day_number}/add-exercise`,
        { exercise_id, section: addSection },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ejercicio agregado');
      if (res.data?.routine) setDetail({ ...detail, routine: res.data.routine });
      setAddingToDayModal(null);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar');
    } finally {
      setAddingExercise(false);
    }
  };

  const removeExercise = async (dayNumber, exerciseId, exerciseName) => {
    const userId = getUserId();
    if (!userId) return;
    if (!window.confirm(`¿Eliminar "${exerciseName}" del día ${dayNumber}?`)) return;
    const key = `${dayNumber}-${exerciseId}`;
    setDeletingKey(key);
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userId}/routine/day/${dayNumber}/exercise/${exerciseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ejercicio eliminado');
      if (res.data?.routine) setDetail({ ...detail, routine: res.data.routine });
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeletingKey(null);
    }
  };

  const filtered = items.filter(it => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (it.user_email || '').toLowerCase().includes(q) || (it.user_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#09090b] p-6 md:p-10" data-testid="admin-user-routines">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-pink-400" />
              </div>
              <h1 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                Rutinas de <span className="text-pink-500">Usuarios</span>
              </h1>
            </div>
            <p className="text-zinc-400 text-sm">{items.length} rutinas activas</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email o nombre..."
            className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-full text-white text-sm focus:border-pink-500 outline-none"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-3xl p-12 text-center">
            <ClipboardList className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">Aún no hay rutinas generadas</p>
          </div>
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-3xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-900/80">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Usuaria</th>
                  <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Días</th>
                  <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Ciclo</th>
                  <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Semanas activa</th>
                  <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Completados</th>
                  <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Creada</th>
                  <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filtered.map(it => (
                  <tr key={it.routine_id} className="hover:bg-zinc-800/30 transition-colors" data-testid={`routine-row-${it.routine_id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-white text-sm">{it.user_name || '—'}</div>
                      <div className="text-xs text-zinc-500">{it.user_email}</div>
                    </td>
                    <td className="text-center px-6 py-4 text-zinc-300 text-sm">{it.num_days}</td>
                    <td className="text-center px-6 py-4">
                      <span className={`inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full border ${it.current_phase === 'intensity_increased' ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-zinc-700 bg-zinc-800 text-zinc-300'}`}>
                        {it.cycle_number === 1 ? 'Inicial' : `Ciclo ${it.cycle_number}`}{it.current_phase === 'intensity_increased' ? ' · ↑' : ''}
                      </span>
                    </td>
                    <td className="text-center px-6 py-4 text-zinc-300 text-sm">{it.weeks_active}</td>
                    <td className="text-center px-6 py-4 text-emerald-400 text-sm font-medium">{it.days_completed_count}</td>
                    <td className="text-center px-6 py-4 text-zinc-400 text-xs">{formatDate(it.created_at)}</td>
                    <td className="text-center px-6 py-4">
                      <button onClick={() => openDetail(it.routine_id)} className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider" data-testid={`view-routine-${it.routine_id}`}>
                        <Eye className="w-3.5 h-3.5" /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {(detail || loadingDetail) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-3xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <h2 className="text-xl font-bold text-white">Detalle de rutina</h2>
              <button onClick={() => setDetail(null)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {loadingDetail ? (
                <div className="text-center py-12"><Loader2 className="w-8 h-8 text-pink-500 animate-spin mx-auto" /></div>
              ) : detail ? (
                <>
                  {/* User info */}
                  <div className="bg-zinc-800/50 rounded-2xl p-5 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <User className="w-5 h-5 text-pink-400" />
                      <div>
                        <div className="font-bold text-white">{detail.user?.name || '—'}</div>
                        <div className="text-xs text-zinc-500">{detail.user?.email}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                      <div>
                        <span className="text-zinc-500 text-xs uppercase tracking-wider block">Días</span>
                        <span className="text-white font-bold">{detail.routine.form_snapshot?.num_days}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs uppercase tracking-wider block">Nivel</span>
                        <span className="text-white font-bold">{cap(detail.routine.form_snapshot?.level)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs uppercase tracking-wider block">Lugar</span>
                        <span className="text-white font-bold">{cap(detail.routine.form_snapshot?.location)}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs uppercase tracking-wider block">Objetivo</span>
                        <span className="text-white font-bold">{cap(detail.routine.form_snapshot?.objetivo)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Cardio */}
                  {detail.routine.cardio && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 mb-6 flex items-center gap-3">
                      <Activity className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <div className="text-sm">
                        <span className="text-emerald-300 font-medium">{detail.routine.cardio.sessions_per_week} sesiones cardio:</span>{' '}
                        <span className="text-zinc-300">{detail.routine.cardio.type}</span>
                      </div>
                    </div>
                  )}

                  {/* Días con ejercicios */}
                  <div className="space-y-4">
                    {(detail.routine.days || []).map(day => (
                      <div key={day.day_number} className="bg-zinc-800/50 rounded-2xl p-5">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-pink-500 text-white font-bold flex items-center justify-center text-sm flex-shrink-0">{day.day_number}</div>
                            <div className="min-w-0">
                              <span className="text-white font-bold">Día {day.day_number}</span>
                              <p className="text-xs text-zinc-500 truncate">{day.focus}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => regenerateDay(day.day_number)}
                            disabled={regeneratingDay !== null}
                            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-pink-400 hover:text-pink-300 disabled:opacity-50 disabled:cursor-not-allowed bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 rounded-full px-3 py-1.5 transition-colors flex-shrink-0"
                            data-testid={`regenerate-day-${day.day_number}`}
                            title={`Regenerar día ${day.day_number}`}
                          >
                            {regeneratingDay === day.day_number ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3.5 h-3.5" />
                            )}
                            <span>{regeneratingDay === day.day_number ? 'Regenerando...' : 'Regenerar'}</span>
                          </button>
                        </div>
                        <ul className="space-y-1.5 text-sm">
                          {(day.exercises || []).map((ex, i) => {
                            const delKey = `${day.day_number}-${ex.exercise_id}`;
                            return (
                              <li key={i} className="flex items-center justify-between gap-3 text-zinc-300 group">
                                <span className="flex-1 min-w-0 truncate">
                                  <span className={`text-[10px] uppercase tracking-wider font-bold mr-2 ${ex.section === 'activacion' ? 'text-blue-400' : ex.section === 'principal' ? 'text-pink-400' : 'text-emerald-400'}`}>[{ex.section?.charAt(0).toUpperCase()}]</span>
                                  {ex.name}
                                  {ex.muscle_group && <span className="text-zinc-500 text-[10px] ml-2">({ex.muscle_group})</span>}
                                  {ex.technique && <span className="text-amber-400 text-xs ml-2">+ {ex.technique.name}</span>}
                                </span>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-zinc-500 text-xs whitespace-nowrap">{ex.sets}×{ex.reps}</span>
                                  <button
                                    onClick={() => openReplaceModal(day.day_number, ex)}
                                    className="p-1 text-sky-400 hover:text-sky-300"
                                    title="Cambiar este ejercicio"
                                    data-testid={`replace-ex-${day.day_number}-${ex.exercise_id}`}
                                  >
                                    <ArrowLeftRight className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => removeExercise(day.day_number, ex.exercise_id, ex.name)}
                                    disabled={deletingKey === delKey}
                                    className="p-1 text-red-400 hover:text-red-300 disabled:opacity-30"
                                    title="Quitar este ejercicio"
                                    data-testid={`remove-ex-${day.day_number}-${ex.exercise_id}`}
                                  >
                                    {deletingKey === delKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                        <button
                          onClick={() => openAddModal(day.day_number)}
                          className="mt-3 w-full text-xs font-bold uppercase tracking-wider text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 border-dashed rounded-xl py-2 flex items-center justify-center gap-1.5 transition-colors"
                          data-testid={`add-ex-day-${day.day_number}`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Agregar ejercicio
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar ejercicio manualmente */}
      {addingToDayModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-2xl my-8" data-testid="add-exercise-modal">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <div>
                <h2 className="text-xl font-bold text-white">{addingToDayModal.replace ? 'Cambiar ejercicio' : 'Agregar ejercicio'}</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Día {addingToDayModal.day_number}
                  {addingToDayModal.replace && <> · reemplazando <span className="text-zinc-300">{addingToDayModal.replace.name}</span></>}
                </p>
              </div>
              <button onClick={() => setAddingToDayModal(null)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {!addingToDayModal.replace && <div className="grid grid-cols-3 gap-2">
                {['activacion', 'principal', 'complementario'].map(s => (
                  <button
                    key={s}
                    onClick={() => setAddSection(s)}
                    className={`px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-colors ${addSection === s ? 'bg-pink-500 border-pink-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-pink-400'}`}
                    data-testid={`section-toggle-${s}`}
                  >
                    {cap(s)}
                  </button>
                ))}
              </div>}
              <input
                type="text"
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                placeholder="Buscar ejercicio por nombre o músculo..."
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-sm focus:border-pink-500 outline-none"
                data-testid="add-exercise-search"
              />
              <div className="max-h-96 overflow-y-auto divide-y divide-zinc-800 border border-zinc-800 rounded-xl">
                {libraryExercises
                  .filter(ex => {
                    const q = addSearch.toLowerCase().trim();
                    if (!q) return true;
                    return (ex.name || '').toLowerCase().includes(q) || (ex.muscle_group || '').toLowerCase().includes(q);
                  })
                  .slice(0, 200)
                  .map(ex => {
                    const usedDays = usedInRoutine[ex.exercise_id] || [];
                    const inThisDay = usedDays.includes(addingToDayModal.day_number);
                    const otherDays = usedDays.filter(d => d !== addingToDayModal.day_number);
                    return (
                    <button
                      key={ex.exercise_id}
                      onClick={() => (addingToDayModal.replace ? replaceExercise(ex.exercise_id) : addExercise(ex.exercise_id))}
                      disabled={addingExercise || inThisDay}
                      className="w-full text-left px-4 py-3 hover:bg-zinc-800 transition-colors flex items-center justify-between gap-3 disabled:opacity-50"
                      data-testid={`add-ex-${ex.exercise_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white font-medium truncate">{ex.name}</p>
                        <p className="text-xs text-zinc-500">
                          {cap(ex.muscle_group)} · {cap(ex.exercise_type)}
                          {inThisDay && <span className="text-amber-400 ml-2">Ya está en este día</span>}
                          {!inThisDay && otherDays.length > 0 && <span className="text-zinc-400 ml-2">También en el día {otherDays.join(', ')}</span>}
                        </p>
                      </div>
                      {addingToDayModal.replace
                        ? <ArrowLeftRight className="w-4 h-4 text-sky-400 flex-shrink-0" />
                        : <Plus className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    </button>
                    );
                  })}
                {libraryExercises.length === 0 && (
                  <p className="text-center text-zinc-500 py-8 text-sm">Cargando biblioteca...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserRoutines;
